import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { baseUrl } from "components/shared/apiCrudBuilder";
import { AuthenticationContext } from "components/context/AuthenticationProvider";
import { useContext } from "react";
import { SnackbarContext } from "components/context/SnackbarProvider";
import { retryOnError } from "components/shared/apiCrudBuilder";

const path = `${baseUrl}/deal`;

export const keyName = "Deal";

export const useAllDeal = (params) => {
  const { appAxios } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [`all_${keyName}`, params],
    queryFn: () => {
      return appAxios.get(`${path}/`, { params: params }).then((res) => {
        const storeInCache = (item) => {
          queryClient.setQueryData([keyName, `${item.id}`], item, {
            staleTime: 10000,
            cacheTime: 10000,
          });
        };
        if (params?.display_mode === "COMMITTEE") {
          res.data.current_deals.forEach((item) =>
            item.deals.forEach(storeInCache),
          );
          res.data.investment_deals.forEach((item) =>
            item.deals.forEach(storeInCache),
          );
          res.data.new_deals.forEach((item) =>
            item.deals.forEach(storeInCache),
          );
        } else {
          res.data.results.forEach(storeInCache);
        }

        return res.data;
      });
    },
    enabled: params !== null,
    retry: retryOnError,
  });
};

export const useDeal = (id, options) => {
  const { appAxios } = useContext(AuthenticationContext);

  return useQuery({
    staleTime: 10000,
    cacheTime: 10000,
    ...options,
    queryKey: [keyName, `${id}`],
    queryFn: () => appAxios.get(`${path}/${id}/`).then((res) => res.data),
    retry: retryOnError,
  });
};

export const useCreateDeal = (options) => {
  const queryClient = useQueryClient();
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: (newObject) =>
      appAxios.post(`${path}/`, newObject).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      queryClient.invalidateQueries({ queryKey: ["User", "countDeals"] });
      queryClient.invalidateQueries({ queryKey: ["CompanyDealAndSourcing"] });
    },
    onError: displayError,
  });
};

export const useUpdateDeal = (id, options) => {
  const queryClient = useQueryClient();
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: (updates) =>
      appAxios.put(`${path}/${id}/`, updates).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyName, `${id}`] });
      queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      queryClient.invalidateQueries({ queryKey: ["User", "countDeals"] });
      queryClient.invalidateQueries({ queryKey: ["CompanyDealAndSourcing"] });
    },
    onError: displayError,
  });
};

export const useDeleteDeal = (id, options) => {
  const queryClient = useQueryClient();
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: () => appAxios.delete(`${path}/${id}/`).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyName, `${id}`] });
      queryClient.invalidateQueries({ queryKey: [`all_${keyName}`] });
      queryClient.invalidateQueries({ queryKey: ["User", "countDeals"] });
      queryClient.invalidateQueries({ queryKey: ["CompanyDealAndSourcing"] });
    },
    onError: displayError,
  });
};

export const useValidateCommitteeMeeting = (options) => {
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: (dealFlowId) =>
      appAxios
        .post(`${path}/committee_minute/`, { deal_flow: dealFlowId })
        .then((res) => res.data),
    onError: displayError,
  });
};

export const useAllTeamSources = (options) => {
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useQuery({
    ...options,
    queryKey: ["all_TeamSources"],
    queryFn: () =>
      appAxios.get(`${path}/team_sources/`).then((res) => res.data),
    cacheTime: 3600000,
    staleTime: 3600000,
    onError: displayError,
  });
};

export const useGetSummary = (options) => {
  const { appAxios } = useContext(AuthenticationContext);
  const { displayError } = useContext(SnackbarContext);

  return useMutation({
    ...options,
    mutationFn: (comment) =>
      appAxios
        .post(`${baseUrl}/summary/`, { comment: comment })
        .then((res) => res.data),
    onError: displayError,
  });
};
